import React, { useState, useEffect } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Plus, Trash2, Edit2, X, MapPin, ChefHat, PhoneCall, Inbox, Utensils, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Tea Break', 'Snack'];

function Modal({ meal, onSave, onClose }) {
  const [form, setForm] = useState(meal || {
    day: '', date: '', mealType: 'Lunch', vendor: '', restaurantAddress: '',
    pricePerHead: '', vegCount: 20, nonVegCount: 0, jainCount: 0,
    pocName: '', pocPhone: '', menu: '', status: 'planned', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = ((parseFloat(form.pricePerHead) || 0) * ((parseInt(form.vegCount, 10) || 0) + (parseInt(form.nonVegCount, 10) || 0)));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.mealType) { setError('Meal type is required.'); return; }
    if (form.pricePerHead && Number(form.pricePerHead) < 0) { setError('Price per head cannot be negative.'); return; }
    if ((parseInt(form.vegCount, 10) || 0) < 0 || (parseInt(form.nonVegCount, 10) || 0) < 0) { setError('Meal counts cannot be negative.'); return; }
    setSaving(true);
    setError('');
    try { await onSave(form); } catch { setError('Failed to save. Please try again.'); setSaving(false); }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{meal ? 'Edit Dining Parameters' : 'Add Dining Schedule'}</h3>
            <p className="text-xs text-slate-500 mt-1">Configure diet counters, menu details and catering partners</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Day Offset</label>
              <input 
                value={form.day} 
                onChange={(e) => set('day', e.target.value)} 
                placeholder="Day 1"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Meal / Dining Date</label>
              <input 
                type="date" 
                value={form.date} 
                onChange={(e) => set('date', e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Service Period</label>
              <select 
                value={form.mealType} 
                onChange={(e) => set('mealType', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {MEAL_TYPES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Caterer / Restaurant Name</label>
              <input 
                value={form.vendor} 
                onChange={(e) => set('vendor', e.target.value)} 
                placeholder="Royal Kitchens Inc."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Restaurant / Catering Address</label>
            <input 
              value={form.restaurantAddress || ''} 
              onChange={(e) => set('restaurantAddress', e.target.value)} 
              placeholder="e.g. 1st Ave, beachfront resort ground"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Unit Cost Price (INRhead)</label>
              <input 
                type="number" 
                value={form.pricePerHead} 
                onChange={(e) => set('pricePerHead', e.target.value)} 
                placeholder="450"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 font-semibold outline-none" 
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Catering Workflow Status</label>
              <select 
                value={form.status} 
                onChange={(e) => set('status', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {['planned', 'confirmed', 'ordered', 'done'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-150 p-4 rounded-xl">
            <div className="space-y-1 text-center">
              <label className="text-[10px] font-bold text-emerald-700 uppercase block">🟢 Vegetarian</label>
              <input 
                type="number" 
                value={form.vegCount} 
                onChange={(e) => set('vegCount', e.target.value)} 
                min={0}
                className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-center font-bold text-slate-800" 
              />
            </div>
            <div className="space-y-1 text-center">
              <label className="text-[10px] font-bold text-red-700 uppercase block">🔴 Non-Vegetarian</label>
              <input 
                type="number" 
                value={form.nonVegCount} 
                onChange={(e) => set('nonVegCount', e.target.value)} 
                min={0}
                className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-center font-bold text-slate-800" 
              />
            </div>
            <div className="space-y-1 text-center">
              <label className="text-[10px] font-bold text-amber-700 uppercase block">🟡 Jain Count</label>
              <input 
                type="number" 
                value={form.jainCount} 
                onChange={(e) => set('jainCount', e.target.value)} 
                min={0}
                className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-center font-bold text-slate-800" 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Catering POC Name</label>
              <input 
                value={form.pocName || ''} 
                onChange={(e) => set('pocName', e.target.value)} 
                placeholder="Chef Mario"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none" 
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Catering POC Phone</label>
              <input 
                value={form.pocPhone || ''} 
                onChange={(e) => set('pocPhone', e.target.value)} 
                placeholder="+91-XXXXX-XXXXX"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none" 
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Dining Menu Draft</label>
            <textarea 
              value={form.menu} 
              onChange={(e) => set('menu', e.target.value)} 
              rows={2} 
              placeholder="Starters: Paneer Tikka, Tandoori Wings. Mains: Butter Masala, Roti, Biryani..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white resize-none" 
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Cooking Instructions</label>
            <textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Keep spices moderate. Dairy-free alternative on table 1..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white resize-none" 
            />
          </div>

          {total > 0 && (
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-between text-indigo-950 text-xs">
              <span className="font-bold uppercase tracking-wider">Calculated Dining Cost:</span>
              <strong className="font-mono text-sm font-bold">₹{total.toLocaleString('en-IN')}</strong>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="animate-spin" />}
              {meal ? 'Save Meal Details' : 'Deploy Meal Scheme'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusClass = { 
  planned: 'sh-badge-warning', 
  confirmed: 'sh-badge-info font-bold', 
  ordered: 'sh-badge-info font-bold', 
  done: 'sh-badge-success' 
};

export default function Meals() {
  const { data: meals, loading } = useStore('meals');
  const [modal, setModal] = useState(null);

  const save = async (form) => {
    await put('meals', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Meal plan updated.' : 'Meal plan added.');
  };

  const del = async (id) => {
    const ok = await confirm('Delete this meal configuration?', 'Delete Meal Plan');
    if (ok) {
      try {
        await remove('meals', id);
        window.dispatchEvent(new CustomEvent('planner:data-change'));
        toast.success('Meal plan deleted.');
      } catch {
        toast.error('Failed to delete meal plan.');
      }
    }
  };

  const totalCost = meals.reduce((s, m) => s + ((parseFloat(m.pricePerHead) || 0) * ((parseInt(m.vegCount, 10) || 0) + (parseInt(m.nonVegCount, 10) || 0))), 0);
  const totalVeg = meals.reduce((s, m) => s + (parseInt(m.vegCount, 10) || 0), 0);
  const totalNonVeg = meals.reduce((s, m) => s + (parseInt(m.nonVegCount) || 0), 0);

  if (loading) return <PageLoader label="Loading meal plans…" sublabel="Fetching dining and catering" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <ChefHat style={{ color: 'var(--accent)' }} size={24} />
            <span>Offsite Dining & Catering</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Configure breakfast slots, team banquets, vegetarian splits, and contractor hotlines
          </p>
        </div>
        <button 
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition inline-flex items-center gap-2 self-start sm:self-auto shadow-sm" 
          onClick={() => setModal('new')}
        >
          <Plus size={16} />
          <span>Add Dining slot</span>
        </button>
      </div>

      {/* Corporate Dining Counters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Vegetarian Plates</span>
            <span className="text-xs">🟢</span>
          </div>
          <div className="sh-stat-value text-slate-900">{totalVeg}</div>
          <p className="text-xs text-slate-500 font-medium">Accumulated day-wise plant diets</p>
        </div>

        <div className="sh-stat-card border-l-4 border-red-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Non-Vegetarian Plates</span>
            <span className="text-xs">🔴</span>
          </div>
          <div className="sh-stat-value text-slate-900">{totalNonVeg}</div>
          <p className="text-xs text-slate-500 font-medium">Accumulated beef/poultry/fish diets</p>
        </div>

        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head">
            <span className="sh-stat-label font-display">Calculated Dining Cost</span>
            <span className="text-xs">🍽️</span>
          </div>
          <div className="sh-stat-value text-slate-900">₹{totalCost.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500 font-medium">Accumulated day-wise food invoice total</p>
        </div>
      </div>

      {/* Dining Cards Matrix */}
      {meals.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800">Dining timeline empty</h3>
          <p className="text-sm text-slate-500 mt-1">Deploy dinner banquets or breakfast slot schemes to organize catering</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {meals.map((m) => { 
            const heads = (parseInt(m.vegCount) || 0) + (parseInt(m.nonVegCount) || 0); 
            const cost = (parseFloat(m.pricePerHead) || 0) * heads; 
            return (
              <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 hover:border-slate-300 transition duration-150 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 tracking-tight flex items-center gap-1.5">
                        <Utensils size={15} className="text-slate-400" />
                        <span>{m.mealType}</span>
                      </h3>
                      <span className="text-xs text-slate-500 font-mono font-medium block mt-0.5">🕒 {m.day || m.date || 'Unscheduled'}</span>
                    </div>
                    <span className={`sh-badge ${statusClass[m.status] || 'sh-badge-outline'}`}>{m.status}</span>
                  </div>

                  {/* Vendor Core */}
                  <div className="space-y-1.5 text-xs text-slate-500 font-medium pt-1">
                    <div className="flex items-center gap-1.5 text-slate-805 text-slate-800">
                      <strong>🍲 Caterer: {m.vendor || 'Caterer TBD'}</strong>
                    </div>
                    {m.restaurantAddress && (
                      <div className="flex items-center gap-1">
                        <MapPin size={11} className="text-slate-400 shrink-0" />
                        <span className="truncate">{m.restaurantAddress}</span>
                      </div>
                    )}
                    {m.pocName && (
                      <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 p-2 rounded-lg w-max max-w-full mt-2">
                        <span>POC: <strong className="text-slate-800">{m.pocName}</strong></span>
                        {m.pocPhone && (
                          <a 
                            href={`tel:${m.pocPhone}`} 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-slate-200 bg-white hover:border-slate-400 rounded-md font-semibold text-slate-800 shadow-xs"
                          >
                            <PhoneCall size={10} />
                            <span>Dial</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Splits view */}
                  <div className="flex items-center gap-4 text-xs font-semibold py-2 px-3 border border-slate-100 bg-slate-50/50 rounded-xl justify-center sm:justify-start">
                    <span className="flex items-center gap-1 text-emerald-850">🟢 Veg: <strong className="text-slate-900">{m.vegCount || 0}</strong></span>
                    <span className="flex items-center gap-1 text-red-850 animate-none">🔴 Non-Veg: <strong className="text-slate-900">{m.nonVegCount || 0}</strong></span>
                    {parseInt(m.jainCount) > 0 && (
                      <span className="flex items-center gap-1 text-amber-850">🟡 Jain: <strong className="text-slate-900">{m.jainCount}</strong></span>
                    )}
                  </div>

                  {/* Menu details */}
                  {m.menu && (
                    <div className="space-y-1 text-xs">
                      <span className="text-slate-400 font-bold uppercase tracking-wider block">Dining Menu:</span>
                      <p className="bg-slate-50 border border-slate-150 p-2.5 rounded-lg text-slate-700 leading-relaxed font-normal">
                        {m.menu}
                      </p>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-4 mt-2">
                  <div className="text-xs text-indigo-950 font-semibold bg-indigo-50/50 border border-indigo-100 px-2 py-1 rounded">
                    ₹{m.pricePerHead || 0}/head | Total ₹{cost.toLocaleString('en-IN')}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button 
                      className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-50 border border-transparent hover:border-slate-200 rounded-lg transition" 
                      onClick={() => setModal(m)}
                    >
                      <Edit2 size={13} />
                    </button>
                    <button 
                      className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-lg transition" 
                      onClick={() => del(m.id)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ); 
          })}
        </div>
      )}

      {modal && <Modal meal={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
}
